import React, { useEffect, useState } from 'react';
import { Image, ActivityIndicator } from 'react-native';
import { SocialComponentApi, SocialPodcastHelpers } from './api';
import type { PlayerPodcastEpisodeProps, PlayerPodcastProps, PodcastEpisodesProps } from '../types';
import { Icons } from '../Components';
import Slider from '../Components/Slider';
import { Audio, type AVPlaybackStatusSuccess } from 'expo-av';
import { Button, Text, View } from '../Components/Themed';
import { useColors } from '../constants/useColors';

Audio.setAudioModeAsync({
    allowsRecordingIOS: false,
    staysActiveInBackground: true,
    playsInSilentModeIOS: true,
    shouldDuckAndroid: true,
    playThroughEarpieceAndroid: false
 })

type AudioPlayerProps = {
    podcast_episode_id:string,
    player_id?:string,
    onSelectEpisode?:(data:{ podcast_episode:PodcastEpisodesProps, player_podcast?:PlayerPodcastProps, player_podcast_episode?:PlayerPodcastEpisodeProps }) => void,
    style?:any,
    onClose:() => void
}
const AudioPlayer = ({podcast_episode_id, player_id, style, onClose, onSelectEpisode}:AudioPlayerProps) => {
    const Colors = useColors();    
    const [ audio_size, setAudioSize ] = useState({ width:50 });
    const [ audio_data, setAudioData ] = useState<{
        audio_loading:boolean,
        podcast_episode?:PodcastEpisodesProps,
        audio_player?:Audio.Sound,
    }>({
        audio_loading:false,
    });
    const [ audio_status, setAudioStatus ] = useState<AVPlaybackStatusSuccess|undefined>(undefined)
    const curr_time = (audio_status?.positionMillis ?? 0) / 1000
    const dur_time = (audio_status?.durationMillis && !isNaN(audio_status.durationMillis) ? audio_status.durationMillis : 1000000) / 1000
    const rem_time = dur_time - curr_time < 0 ? 0 : dur_time - curr_time
    const { audio_loading, podcast_episode, audio_player } = audio_data;
     const [ player_data, setPlayerData ] = useState<{
        player_loading:boolean,
        player_podcast?:PlayerPodcastProps,
        player_podcast_episode?:PlayerPodcastEpisodeProps
    }>({
        player_loading: false
    });
    const { player_loading, player_podcast, player_podcast_episode } = player_data;

    useEffect(() => {
        SocialComponentApi.setEnvironment();
        getAudioData(podcast_episode_id);
    },[podcast_episode_id]);

    const getAudioData = async(pod_episode_id:string) => {
        setAudioData({ ...audio_data, audio_loading:true })
        const episode = await SocialComponentApi.getPodcastEpisodeById(pod_episode_id);
        const { sound, status } = await Audio.Sound.createAsync({ uri: episode?.url ?? ''})
        setAudioStatus(status as AVPlaybackStatusSuccess)
        sound.setProgressUpdateIntervalAsync(1000);
        sound.setOnPlaybackStatusUpdate(status => setAudioStatus(status as AVPlaybackStatusSuccess));
        
        if(player_id){
            let pdata = await getPlayerData(pod_episode_id);
            if(pdata?.player_podcast_episode){
                await sound.setPositionAsync(pdata.player_podcast_episode.pause_timestamp)
            }
        }

        setAudioData({
            ...audio_data,
            audio_loading: false,
            podcast_episode: episode,
            audio_player: sound,
        })
    }

    const getPlayerData = async(pod_episode_id:string) => {
        setPlayerData({ ...player_data, player_loading: true });
        const p_data = await SocialComponentApi.getPlayerPodcastEpisode(pod_episode_id);
        setPlayerData({ 
            ...player_data,
            player_loading: false,
            player_podcast:p_data?.player_podcast,
            player_podcast_episode:p_data?.player_podcast_episode
        });
        return p_data
    }

    const startAudio = async() => {
        if(!audio_player){ return alert('Unable to play audio at this time') }
        await audio_player.playAsync();
        
        if(player_id){
            setPlayerData({ ...player_data, player_loading: true });
            const p_resp = await SocialComponentApi.startPodcastEpisode(podcast_episode_id);
            setPlayerData({ ...player_data, player_loading: false, player_podcast: p_resp?.player_podcast, player_podcast_episode: p_resp?.player_podcast_episode });
        }
    }

    const pauseAudio = async() => {
        if(!audio_player){ return alert('Unable to play audio at this time') }

        const status:any = await audio_player.pauseAsync();
        let cur_time = status.positionMillis ?? 0

        if(player_id){
            setPlayerData({ ...player_data, player_loading: true });
            const p_resp = await SocialComponentApi.pausePodcastEpisode(podcast_episode_id, cur_time);
            setPlayerData({ ...player_data, player_loading: false, player_podcast, player_podcast_episode: p_resp });
        }

    }

    const handleStartSliding = () => {
        if(!audio_player){ return }
        audio_player.pauseAsync()
    }


    const handleManualTimeChange = async(val:number) => {
        //updat manual time!!
        if(!audio_player){ return }
        await audio_player.setPositionAsync(val * 1000);
        await audio_player.playAsync();
    }

    return (
        <View float type='row' style={{ flex:1, borderRadius:8, ...style }} onLayout={(ev) => {
            const { width } = ev.nativeEvent.layout
            setAudioSize({ width })
        }}>
            {!podcast_episode || audio_loading ?
            <ActivityIndicator size='large' color={Colors.text.h1} style={{ alignSelf:'center' }} />
            :
            <View transparent type='row' style={{ flex:1 }}>
                <View transparent>
                    <Image
                        source={{ uri: SocialPodcastHelpers.getEpisodeImage(podcast_episode) }}
                        style={{ width: audio_size.width * 0.135, height: audio_size.width * 0.135 }}
                        resizeMode='cover'
                    />
                    <View transparent style={{ position:'absolute', top:0, left:0, right:0, bottom:0, justifyContent:'center', alignItems:'center' }}>
                        {audio_status && !audio_status.isPlaying ?
                        <Button  float style={{ borderRadius:100, padding:10 }} onPress={() => startAudio() }>
                            <Icons.PlayIcon size={12} color={Colors.text.success} />
                        </Button>
                        :audio_status &&audio_status.isPlaying ?
                        <Button  float style={{ borderRadius:100, padding:10 }} onPress={() => pauseAudio() }>
                            <Icons.PauseIcon size={12} color={Colors.text.error} />
                        </Button>
                        :<></>}
                    </View>
                </View>
                <View transparent type='row' style={{ flex:1 }}>
                    <View transparent style={{ flex:1, padding:5, marginBottom:5 }}>
                        <Button 
                            disabled={!onSelectEpisode}
                            onPress={() => onSelectEpisode? onSelectEpisode({ podcast_episode: podcast_episode, player_podcast:player_podcast, player_podcast_episode:player_podcast_episode }) : console.log('')}
                            transparent type='text' style={{ padding:5 }}>
                            <Text size={14} theme='h1'>{podcast_episode.title}</Text>
                        </Button>
                        <View transparent style={{ flexDirection:'row', alignItems:'center' }}>
                            <Text style={{ padding:5, width:50, alignSelf:'center' }} size={12} theme='description'>{SocialPodcastHelpers.formatTime(curr_time)}</Text>
                            {audio_status ?
                            <Slider
                                width={audio_size.width * 0.47}
                                value={curr_time}
                                minimum_value={0}
                                maximum_value={dur_time}
                                onSlidingStart={() => handleStartSliding()}
                                onSlidingComplete={(val) => handleManualTimeChange(val)}
                            />
                            :<></>}
                            <Text style={{ padding:5, width:50, alignSelf:'center' }} size={12} theme='description'>-{SocialPodcastHelpers.formatTime(isNaN(rem_time)?0:rem_time)}</Text>
                        </View>
                        {player_loading || audio_loading ?
                        <View transparent style={{ position:'absolute', top:0, left:0, bottom:0, right:0, justifyContent:'center' }}>
                            <ActivityIndicator size='small' color={Colors.text.h1} />
                        </View>
                        :<></>}
                    </View>
                    <View transparent style={{ marginLeft:10, marginRight:10, flexDirection:'row', alignItems:'center' }}>
                        <Button
                            float
                            style={{ borderRadius:100, height:30, width:30, justifyContent:'center', alignItems:'center', padding:0 }}
                            title='X'
                            type='close'
                            onPress={async() => {
                                await pauseAudio();
                                onClose();
                            }}
                        />
                        
                    </View>
                </View>
            </View>
            }
        </View>

    )
}

export default AudioPlayer